import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

/**
 * Ein bearbeitbarer Bereich, den React **nicht** verwaltet (Befund G10).
 *
 * Vorher saß `contentEditable` auf einem von React gerenderten Teilbaum. Jeder
 * Neuaufbau der Seite ersetzte dessen Kinder und verwarf damit die
 * Handeingaben — reproduzierbar über den Zeitgeber hinter „kopiert", der nach
 * zwei Sekunden `setCopied(false)` auslöst.
 *
 * Hier hat der Knoten in JSX **keine Kinder**. React hat nichts zu
 * vergleichen und lässt den Inhalt in Ruhe; gesetzt wird er einmalig beim
 * Einhängen. `getHtml()` liefert genau den Inhalt dieses Knotens — ohne den
 * umschließenden Knoten selbst, sonst wüchse pro Bearbeitungszyklus eine
 * zusätzliche Verschachtelung.
 */
export interface EditableHtmlHandle {
  /** Aktueller Inhalt — nur die Kinder, nicht der Rahmen. */
  getHtml: () => string;
  /**
   * Hat der Mensch seit dem Einhängen etwas geändert?
   *
   * Verglichen wird gegen den Stand, den der Browser **nach** dem Setzen von
   * `innerHTML` zurückgibt, nicht gegen `initialHtml` selbst: Beim Setzen
   * normalisiert der Browser das Markup (Attributreihenfolge, Anführungs-
   * zeichen, implizite `tbody`). Ein Vergleich gegen die Rohvorlage meldete
   * deshalb schon beim bloßen Öffnen des Editors „geändert" — und würde bei
   * jedem Wechsel der Beauftragung grundlos nachfragen.
   */
  isDirty: () => boolean;
}

export const EditableHtml = forwardRef<
  EditableHtmlHandle,
  { initialHtml: string }
>(function EditableHtml({ initialHtml }, ref) {
  const nodeRef = useRef<HTMLDivElement>(null);
  /** Normalisierter Ausgangsstand — Bezugsgröße für `isDirty()`. */
  const baselineRef = useRef<string>("");

  useEffect(() => {
    // Bewusst einmalig: `initialHtml` steht absichtlich nicht in der
    // Abhängigkeitsliste. Ein Nachziehen würde die laufende Bearbeitung
    // überschreiben — genau der Fehler, den diese Komponente behebt.
    if (nodeRef.current) {
      nodeRef.current.innerHTML = initialHtml;
      baselineRef.current = nodeRef.current.innerHTML;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    getHtml: () => nodeRef.current?.innerHTML ?? "",
    isDirty: () => (nodeRef.current?.innerHTML ?? "") !== baselineRef.current,
  }));

  return (
    <div
      ref={nodeRef}
      contentEditable
      suppressContentEditableWarning
      data-testid="report-editor"
    />
  );
});
